import { Button, Typography } from '@mui/material';
import { xor } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { fundsRequestsApi } from 'api';
import {
  CloseDialogResult,
  DataGridColumnDefinition,
  PageHeader,
} from 'components';
import { FUNDS_REQUEST_TRANSACTION_IN_PROGRESS_STATUSES } from 'constants/funds-requests.constants';
import {
  FilterDefinitionType,
  FundRequestTab,
  FundsRequestCompletionType,
  FundsRequestStatus,
  OperationType,
  QueryKey,
} from 'enums';
import { FundsRequests } from 'features/funds-request';
import { useMutation, usePartialQuery, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  FundsRequest,
  FundsRequestFilters,
  Shop,
  ShopGroup,
  User,
} from 'types';

import {
  FundsRequestCompletionDepositDialog,
  FundRequestCompletionData,
} from './FundsRequstCompletionDepositDialog';

type Props = {
  hideTitle?: boolean;
  users?: User[];
  shops?: Shop[];
  shopGroups?: ShopGroup[];
};

export const ProcessFundsRequests: React.FC<Props> = ({
  hideTitle,
  users,
  shops,
  shopGroups,
}) => {
  const { t: tCommon } = useTranslation();
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.funds_request',
  });
  const { role } = useUser();

  const queryResult = usePartialQuery(
    QueryKey.FundsRequests,
    fundsRequestsApi.getAllPaginatedByRole(role, FundRequestTab.Active),
    {
      refetchOnWindowFocus: true,
    },
  );

  const [processInProgress, setProcessInProgress] = useState<string[]>([]);
  const [completeDepositDialogProps, setCompleteDepositDialogProps] = useState<{
    open: boolean;
    data?: FundRequestCompletionData;
  }>({
    open: false,
  });

  const queryClient = useQueryClient();

  const onProcessSuccess = useCallback(() => {
    queryClient.invalidateQueries(QueryKey.FundsRequests);
  }, [queryClient]);

  const { mutate: complete } = useMutation(fundsRequestsApi.processComplete, {
    onSuccess: onProcessSuccess,
    requireSecureConfirm: true,
  });

  const { mutate: cancel } = useMutation(fundsRequestsApi.processCancel, {
    onSuccess: onProcessSuccess,
  });

  const toggleProcessInProgress = useCallback((item: FundsRequest) => {
    setProcessInProgress((prev) => xor(prev, [item.id]));
  }, []);

  const handleCancel = useCallback(
    (item: FundsRequest) => {
      setCompleteDepositDialogProps({
        open: true,
        data: {
          item,
          completionType: FundsRequestCompletionType.Reject,
        },
      });
    },
    [setCompleteDepositDialogProps],
  );

  const handleComplete = useCallback(
    (item: FundsRequest) => {
      setCompleteDepositDialogProps({
        open: true,
        data: {
          item,
          completionType: FundsRequestCompletionType.Approve,
        },
      });
    },
    [setCompleteDepositDialogProps],
  );

  const handleCompleteDepositDialog = useCallback(
    ({ ok, data }: CloseDialogResult<FundRequestCompletionData>) => {
      if (ok && data) {
        toggleProcessInProgress(data.item);
        if (data.completionType === FundsRequestCompletionType.Reject) {
          cancel(data.item, {
            onSettled: () => {
              toggleProcessInProgress(data.item);
              setCompleteDepositDialogProps({ open: false });
            },
          });
        } else if (data.completionType === FundsRequestCompletionType.Approve) {
          complete(
            {
              ...data.item,
              ...(data.item.operationType === OperationType.Withdrawal && {
                hash: data.hash,
                sendTransaction: data.sendTransaction,
              }),
            },
            {
              onSettled: () => {
                toggleProcessInProgress(data.item);
                setCompleteDepositDialogProps({ open: false });
              },
            },
          );
        }
      } else {
        setCompleteDepositDialogProps({ open: false });
      }
    },
    [cancel, complete, toggleProcessInProgress],
  );

  const additionalFilters: FilterDefinition<FundsRequestFilters>[] = useMemo(
    () => [
      {
        label: t('funds_requests.fields.wallet'),
        name: 'wallet',
        type: FilterDefinitionType.Text,
      },
      {
        label: t('funds_requests.fields.hash'),
        name: 'hash',
        type: FilterDefinitionType.Text,
      },
    ],
    [t],
  );

  const additionalColumns = useMemo(
    (): DataGridColumnDefinition<FundsRequest>[] => [
      {
        header: t('funds_requests.fields.disputes'),
        valueGetter: (item: FundsRequest) => (
          <div className="tw-inline-grid tw-grid-cols-1 min-[1650px]:tw-grid-cols-2 tw-gap-2">
            <Typography color="textSecondary">
              {`${tCommon('common.payin')}:`}
            </Typography>
            <Typography className="tw-font-bold">
              {item?.activePayinDisputeCount}
            </Typography>
            <Typography className="tw-font-extrabold" color="textSecondary">
              {`${tCommon('common.payout')}:`}
            </Typography>
            <Typography>{item?.activePayoutDisputeCount}</Typography>
          </div>
        ),
      },
      {
        header: t('funds_requests.fields.actions'),
        valueClassName: 'tw-min-w-[240px]',
        valueGetter: (item) => {
          const isTransactionInProgressStatus =
            FUNDS_REQUEST_TRANSACTION_IN_PROGRESS_STATUSES.includes(
              item.status,
            );

          const isNew = item.status === FundsRequestStatus.New;

          if (!isNew && !isTransactionInProgressStatus) {
            return null;
          }

          return (
            <div className="tw-grid tw-grid-cols-2 tw-gap-2">
              {item.status === FundsRequestStatus.New ? (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={processInProgress.includes(item.id)}
                  onClick={() => handleComplete(item)}
                >
                  {t('process_funds_requests.approve')}
                </Button>
              ) : (
                <div />
              )}
              <Button
                variant="outlined"
                color="error"
                size="small"
                disabled={processInProgress.includes(item.id)}
                onClick={() => handleCancel(item)}
              >
                {t('process_funds_requests.reject')}
              </Button>
            </div>
          );
        },
      },
    ],
    [t, tCommon, processInProgress, handleComplete, handleCancel],
  );

  return (
    <Fragment>
      {!hideTitle && (
        <PageHeader title={t('process_funds_requests.title')} divider={false} />
      )}
      <FundsRequests
        archived={false}
        queryResult={queryResult}
        additionalFilters={additionalFilters}
        additionalColumns={additionalColumns}
        users={users}
        shops={shops}
        shopGroups={shopGroups}
      />
      <FundsRequestCompletionDepositDialog
        {...completeDepositDialogProps}
        onClose={handleCompleteDepositDialog}
      />
    </Fragment>
  );
};
